import React, { useState, useEffect } from "react";
import PhoneInput, { CountryData, PhoneInputProps } from "react-phone-input-2";
import ru from "react-phone-input-2/lang/ru.json";
import "react-phone-input-2/lib/style.css";
import "./style.css";
import { FieldRenderProps } from "react-final-form";
import { configStylesByState, StateStyles } from "./configStylesByState";
import { ERROR_FIELD_ } from "~/configs/consts";
import cn from "classnames";

import styles from "./PhoneField.module.scss";
import { useIsMobile } from "~/hooks";
import { CustomTooltip } from "../title-field/CustomTooltip";
import { AnimatedContainer } from "~/containers";
import { MobileTooltip } from "../MobileTooltip/MobileTooltip";

interface PhoneFieldProps extends FieldRenderProps<string, HTMLElement>, Partial<PhoneInputProps> {
  isAutoFocus?: boolean;
  required?: boolean;
  onlyCountries?: string[];
  titleField?: string;
  tooltip?: string;
}

export const PhoneField: React.FC<PhoneFieldProps> = ({
  input,
  meta,
  isAutoFocus = false,
  disabled = false,
  onlyCountries,
  titleField,
  required,
  tooltip,
  ...props
}) => {
  const isMobile = useIsMobile();
  const [, setCountryInfo] = useState<CountryData | null>(null);
  const [currentCountry, setCurrentCountry] = useState<string>("ru");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);

  const hasError = (meta?.error || meta?.submitError) && meta?.submitFailed;
  const inputId = hasError ? `${ERROR_FIELD_ + input.name}` : undefined;

  const normalizePhoneNumber = (phone: string, countryCode: string = "ru"): string => {
    if (!phone) return phone;

    // Заменяем 8 на +7 только для российских номеров
    if (countryCode === "ru" && phone.startsWith("8") && phone.length > 1) {
      return `+7${phone.slice(1)}`;
    }

    // Если номер начинается с 7 (без плюса) для России, добавляем +
    if (
      countryCode === "ru" &&
      phone.startsWith("7") &&
      !phone.startsWith("+7") &&
      phone.length > 1
    ) {
      return `+${phone}`;
    }

    return phone;
  };

  useEffect(() => {
    const normalizedPhone = normalizePhoneNumber(input.value, currentCountry);
    if (normalizedPhone !== input.value) {
      input.onChange(normalizedPhone);
    }
  }, [input, input.value, currentCountry]);

  const getStateStyles = (): StateStyles => {
    if (hasError && isFocused) return StateStyles.ErrorFocused;
    if (hasError && isHovered) return StateStyles.ErrorHovered;
    if (hasError) return StateStyles.Error;
    if (isFocused) return StateStyles.Focused;
    if (isHovered) return StateStyles.Hover;
    if (disabled) return StateStyles.Disabled;
    return StateStyles.Default;
  };

  const stateStyles = getStateStyles();

  const containerStyle: React.CSSProperties = {
    ...configStylesByState[StateStyles.Default].containerStyle,
    ...configStylesByState[stateStyles].containerStyle
  };

  const inputStyle: React.CSSProperties = {
    ...configStylesByState[StateStyles.Default].inputStyle,
    ...configStylesByState[stateStyles].inputStyle
  };

  const handleCountryChange = (value: string, info: CountryData) => {
    setCountryInfo(info);
    setCurrentCountry(info.countryCode);

    const normalizedValue = normalizePhoneNumber(value, info.countryCode);
    input.onChange(normalizedValue);
  };

  // Обработчик для ручного ввода (на случай, если пользователь вводит 8)
  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement;
    const currentValue = target.value;

    // Если пользователь начинает ввод с 8 и это российский номер, заменяем на +7
    if (
      currentCountry === "ru" &&
      (currentValue === "8" ||
        (currentValue === "" && e.nativeEvent instanceof InputEvent && e.nativeEvent.data === "8"))
    ) {
      setTimeout(() => {
        input.onChange("+7");
      }, 0);
    }
  };

  return (
    <div>
      <div
        className={cn(styles.container, {
          [styles.hasErrorContainer]: hasError,
          [styles.hoveredContainer]: isHovered,
          [styles.errorFocusedContainer]: hasError && isFocused
        })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        id={inputId}
      >
        <div className={styles.containerTitle}>
          <label
            className={cn(styles.label, {
              [styles.text]: required
            })}
          >
            {titleField}
          </label>
          {tooltip && (
            <CustomTooltip tooltip={tooltip} iconClassName={styles.customTooltipQuestion} />
          )}
        </div>
        <PhoneInput
          {...props}
          disabled={disabled}
          value={input.value}
          onChange={handleCountryChange}
          localization={ru}
          onlyCountries={onlyCountries}
          country="ru"
          masks={{ ru: "(...) ...-..-..", ci: ".........." }}
          specialLabel={""}
          areaCodes={{ kz: [7, 6] }}
          inputProps={{
            name: input.name,
            autoFocus: isAutoFocus,
            onBlur: (e: any) => {
              setIsFocused(false);
              input.onBlur(e);
            },
            onFocus: () => setIsFocused(true),
            onBeforeInput: handleBeforeInput
          }}
          inputStyle={{ ...inputStyle, fontSize: isMobile ? 14 : 16 }}
          containerStyle={containerStyle}
          inputClass={cn({
            [styles.hasErrorInput]: hasError,
            [styles.hoveredInput]: isHovered,
            [styles.errorFocusedInput]: hasError && isFocused
          })}
          preserveOrder={["onlyCountries"]}
          preferredCountries={["ru"]}
          enableSearch={true}
          searchPlaceholder="Поиск страны..."
        />
      </div>
      <AnimatedContainer isVisible={isFocused}>
        <MobileTooltip content={tooltip} />
      </AnimatedContainer>
    </div>
  );
};
